import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import { TwilioRegion } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useEffect, useState } from 'react';

import { CopyToClipboard } from '~components/CopyToClipboard/CopyToClipboard';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { TWILIO_REGIONS, TwilioIntegration } from '~hooks/useTwilioIntegrations/models';
import { useTwilioIntegrations } from '~hooks/useTwilioIntegrations/useTwilioIntegrations';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';

interface SetUpTicketChasingDialogProps {
  company: Nullable<{ id: string; legalName: string }>;
  onBusyChange: (isBusy: boolean) => void;
  onClose: () => void;
}

export function SetUpTicketChasingDialog({
  company,
  onBusyChange,
  onClose,
}: SetUpTicketChasingDialogProps) {
  const { toasterStore } = useStores();
  const { getTwilioIntegrations, createTwilioIntegration } = useTwilioIntegrations();
  const [integrations, setIntegrations] = useState<TwilioIntegration[] | null>(null);
  const [region, setRegion] = useState<TwilioIntegration['twilioRegion']>(
    TwilioRegion.US,
  );
  const [isCreating, setIsCreating] = useState(false);
  const [isLoadFailed, setIsLoadFailed] = useState(false);
  const companyId = company?.id;

  useEffect(() => {
    setIntegrations(null);
    setIsLoadFailed(false);
    setRegion(TwilioRegion.US);
    if (!companyId) {
      return;
    }

    const controller = new AbortController();
    const loadIntegrations = async () => {
      try {
        const result = await getTwilioIntegrations(companyId, controller.signal);
        if (!controller.signal.aborted) {
          setIntegrations(result ?? []);
        }
      } catch {
        // The error toast is shown by the hook.
        if (!controller.signal.aborted) {
          setIsLoadFailed(true);
        }
      }
    };
    void loadIntegrations();

    return () => {
      controller.abort();
    };
  }, [companyId, getTwilioIntegrations]);

  useEffect(() => {
    onBusyChange(isCreating);

    return () => {
      onBusyChange(false);
    };
  }, [isCreating, onBusyChange]);

  const canSetUp = integrations?.length === 0;

  const handleClose = () => {
    if (!isCreating) {
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (!companyId) {
      return;
    }

    setIsCreating(true);
    try {
      const created = await createTwilioIntegration(companyId, region);
      if (created) {
        setIntegrations((current) => [...(current ?? []), created]);
        toasterStore.push(alert(t('company.ticket_chasing.created'), AlertTypes.success));
      }
    } catch {
      // The error toast is shown by the hook.
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog isOpen={Boolean(company)} maxWidth="sm" onClose={handleClose}>
      <DialogHeader onClose={handleClose}>
        {t('company.ticket_chasing.title')}
      </DialogHeader>

      <DialogContent>
        {integrations === null ? (
          isLoadFailed ? null : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {integrations.length > 0 ? (
              <>
                <Typography>
                  {t('company.ticket_chasing.existing_description', {
                    name: company?.legalName,
                  })}
                </Typography>
                {integrations.map((integration) => (
                  <CopyToClipboard
                    key={integration.id}
                    textToCopy={integration.phone}
                    sx={{ display: 'flex', width: 'fit-content', gap: 1 }}
                  >
                    <Card variant="outlined" sx={{ p: 1, display: 'flex', gap: 2 }}>
                      <Typography sx={{ fontWeight: 500 }}>
                        {parsePhoneNumberFromString(
                          integration.phone,
                        )?.formatInternational() ?? integration.phone}
                      </Typography>
                      <Typography sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {integration.twilioRegion.toUpperCase()}
                      </Typography>
                    </Card>
                  </CopyToClipboard>
                ))}
              </>
            ) : (
              <>
                <Typography>
                  {t('company.ticket_chasing.description', { name: company?.legalName })}
                </Typography>
                <Typography sx={{ fontWeight: 500 }}>
                  {t('administration.agents.region')}
                </Typography>
                <RadioGroup
                  row
                  value={region}
                  onChange={(event) =>
                    setRegion(event.target.value as TwilioIntegration['twilioRegion'])
                  }
                >
                  {TWILIO_REGIONS.map((twilioRegion) => (
                    <FormControlLabel
                      key={twilioRegion}
                      value={twilioRegion}
                      control={<Radio />}
                      label={twilioRegion.toUpperCase()}
                      disabled={isCreating}
                    />
                  ))}
                </RadioGroup>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <SmallButton
          color="secondary"
          disabled={isCreating}
          onClick={handleClose}
          variant="outlined"
        >
          {t('actions.close')}
        </SmallButton>

        {canSetUp && (
          <SmallLoadingButton
            color="primary"
            loading={isCreating}
            onClick={handleConfirm}
            variant="contained"
          >
            {t('company.ticket_chasing.buy_cta')}
          </SmallLoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
